// client/src/pages/ReportsMaster.js
import React, { useEffect, useState, useMemo } from "react";
import api from "../api/api";
import { CSVLink } from "react-csv";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./ReportsMaster.css";

const OVERSEER_MINISTRY_NAME = "Overseers";
const STAFF_MINISTRY_NAME = "Staff";

const combineName = (row) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "—";

const REMOVE_KEYS = [
  "user_id",
  "ministry_id",
  "id",
  "type",
  "elder_id",
  "event_id",
  "checkin_id",
];

const ReportsMaster = () => {
  const [reportType, setReportType] = useState("attendees");
  const [data, setData] = useState([]);
  const [eventId, setEventId] = useState("");
  const [elderId, setElderId] = useState("");
  const [events, setEvents] = useState([]);
  const [elders, setElders] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [ministryId, setMinistryId] = useState("");
  const [overseerMinistryId, setOverseerMinistryId] = useState("");
  const [staffMinistryId, setStaffMinistryId] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // for Members Without Active Ministry
  const [onlyActive, setOnlyActive] = useState(true);

  const role = localStorage.getItem("adminRole");

  useEffect(() => {
    if (
      reportType === "roster" ||
      reportType === "inactive" ||
      reportType === "no-ministry"
    ) {
      setSortConfig({ key: "last_name", direction: "asc" });
    } else {
      setSortConfig({ key: null, direction: null });
    }
  }, [reportType]);

  useEffect(() => {
    if (role !== "super_admin" && role !== "admin") return;
    const fetchOptions = async () => {
      try {
        const eventRes = await api.get("/events");
        setEvents(eventRes.data);

        const elderRes = await api.get("/users/elders");
        setElders(elderRes.data);

        const minRes = await api.get("/reports/ministries");
        setMinistries(minRes.data);

        const overseerMinistry = minRes.data.find((m) =>
          m.name.toLowerCase().includes(OVERSEER_MINISTRY_NAME.toLowerCase())
        );
        const staffMinistry = minRes.data.find((m) =>
          m.name.toLowerCase().includes(STAFF_MINISTRY_NAME.toLowerCase())
        );
        setOverseerMinistryId(overseerMinistry ? overseerMinistry.id : "");
        setStaffMinistryId(staffMinistry ? staffMinistry.id : "");
      } catch (err) {
        console.error("Error loading events, elders, or ministries:", err);
      }
    };
    fetchOptions();
  }, [role]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        let res;
        switch (reportType) {
          case "attendees":
            res = await api.get("/reports/attendees");
            break;

          case "overseer-attendance":
            if (overseerMinistryId) {
              let url = `/reports/ministry-attendance/${overseerMinistryId}`;
              if (eventId) url += `?event_id=${eventId}`;
              res = await api.get(url);
            }
            break;

          case "staff-attendance":
            if (staffMinistryId) {
              let url = `/reports/ministry-attendance/${staffMinistryId}`;
              if (eventId) url += `?event_id=${eventId}`;
              res = await api.get(url);
            }
            break;

          case "ministry-attendance":
            if (ministryId) {
              let url = `/reports/ministry-attendance/${ministryId}`;
              if (eventId) url += `?event_id=${eventId}`;
              res = await api.get(url);
            }
            break;

          case "ministry-absent":
            if (eventId) {
              const params = ministryId ? { ministry_id: ministryId } : {};
              res = await api.get(`/reports/ministry-absent/${eventId}`, {
                params,
              });
            }
            break;

          case "elder":
            if (elderId) {
              let url = `/reports/elder/${elderId}`;
              if (eventId) url += `?event_id=${eventId}`;
              res = await api.get(url);
            }
            break;

          case "elder-absent":
            if (elderId && eventId)
              res = await api.get(
                `/reports/elder-absent/${elderId}/${eventId}`
              );
            break;

          case "roster":
            if (ministryId) {
              res = await api.get(`/reports/roster/${ministryId}`);
            }
            break;

          // Members without any ACTIVE ministry
          case "no-ministry":
            res = await api.get("/reports/no-active-ministry", {
              params: { active_only: onlyActive ? "true" : "false" },
            });
            break;

          // NEW: Inactive members (u.active=false)
          case "inactive":
            res = await api.get("/reports/inactive-members");
            break;

          default:
            return;
        }
        setData(res ? res.data || [] : []);
      } catch (err) {
        console.error("Report fetch error:", err);
        setData([]);
      }
    };
    fetchReport();
  }, [
    reportType,
    eventId,
    elderId,
    ministryId,
    overseerMinistryId,
    staffMinistryId,
    onlyActive,
  ]);

  /** Columns */
  const columns =
    data.length > 0
      ? (() => {
          // default
          const base = [
            "name",
            ...Object.keys(data[0]).filter(
              (key) =>
                !REMOVE_KEYS.includes(key) &&
                key !== "first_name" &&
                key !== "last_name"
            ),
          ];

          if (reportType === "no-ministry") {
            // members without active ministry -> show ministry column as "--"
            return ["name", "email", "phone", "active", "ministry"];
          }

          if (reportType === "inactive") {
            // include ministries (string already), keep active column for clarity
            return ["name", "email", "phone", "ministries", "active"];
          }

          return base;
        })()
      : [];

  /** Row shaping */
  const processedData = useMemo(() => {
    let items = data.map((row) => ({
      ...row,
      name: combineName(row),
    }));

    if (reportType === "no-ministry") {
      items = items.map((r) => ({
        ...r,
        ministry: "--",
      }));
    }

    if (reportType === "inactive") {
      // ensure ministries is a simple string for table/CSV/PDF
      items = items.map((r) => ({
        ...r,
        ministries: Array.isArray(r.ministries)
          ? r.ministries.join(", ")
          : r.ministries || "",
      }));
    }

    if (sortConfig.key) {
      items.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === "string" && typeof valB === "string") {
          return sortConfig.direction === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        } else {
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        }
      });
    }
    return items;
  }, [data, sortConfig, reportType]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  /** CSV payload */
  const csvData =
    processedData.length > 0
      ? processedData.map((row) =>
          columns.reduce((obj, key) => {
            obj[key] = row[key];
            return obj;
          }, {})
        )
      : [];

  if (role !== "super_admin" && role !== "admin")
    return <p>Access denied. Admins only.</p>;

  /** Generate all (ZIP) */
  const handleGenerateAll = async () => {
    try {
      setDownloading(true);
      const res = await api.post(
        "/reports/generate-all",
        { event_id: eventId || null, formats: ["csv"] },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateStamp = new Date().toISOString().slice(0, 10);
      a.download = `all-ministries-reports-${dateStamp}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Failed to generate ZIP.");
    } finally {
      setDownloading(false);
    }
  };

  /** Email reports */
  const handleEmailReports = async () => {
    if (!eventId) {
      alert("Select an Event to email.");
      return;
    }
    try {
      setEmailing(true);
      await api.post("/email/send-reports", {
        event_id: eventId,
        ministries: "all",
        template_id: "default",
        attach: ["attendance", "absent"],
      });
      alert("Email queued/sent.");
    } catch (e) {
      console.error(e);
      alert("Failed to send email.");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="reports-master">
      <h2>📋 Reports Center</h2>

      <div className="report-filters">
        <select
          onChange={(e) => {
            setReportType(e.target.value);
            setEventId("");
            setElderId("");
            setMinistryId("");
            setData([]);
          }}
          value={reportType}
        >
          <option value="attendees">Full Attendee Report</option>
          <option value="overseer-attendance">
            Overseer Attendance Report
          </option>
          <option value="staff-attendance">Staff Attendance Report</option>
          <option value="ministry-attendance">
            Ministry Attendance Report
          </option>
          <option value="ministry-absent">Ministry Absent Report</option>
          <option value="elder">Elder Report</option>
          <option value="elder-absent">Elder Absent Report</option>
          <option value="roster">Roster Report</option>
          <option value="no-ministry">Members Without Active Ministry</option>
          {/* NEW */}
          <option value="inactive">Inactive Members</option>
        </select>

        {["ministry-attendance", "roster"].includes(reportType) && (
          <select
            value={ministryId}
            onChange={(e) => setMinistryId(e.target.value)}
          >
            <option value="">Select Ministry</option>
            {ministries.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}

        {(reportType === "overseer-attendance" ||
          reportType === "staff-attendance" ||
          reportType === "ministry-attendance") && (
          <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">All Events</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} ({new Date(e.event_date).toLocaleDateString("en-US")})
              </option>
            ))}
          </select>
        )}

        {reportType === "ministry-absent" && (
          <>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
            >
              <option value="">Select Event</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title} (
                  {new Date(e.event_date).toLocaleDateString("en-US")})
                </option>
              ))}
            </select>
            <select
              value={ministryId}
              onChange={(e) => setMinistryId(e.target.value)}
            >
              <option value="">Select Ministry</option>
              {ministries.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </>
        )}

        {(reportType === "elder" || reportType === "elder-absent") && (
          <select value={elderId} onChange={(e) => setElderId(e.target.value)}>
            <option value="">Select Elder</option>
            {elders.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name}
              </option>
            ))}
          </select>
        )}
        {reportType === "elder-absent" && (
          <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Select Event</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} ({new Date(e.event_date).toLocaleDateString("en-US")})
              </option>
            ))}
          </select>
        )}

        {reportType === "no-ministry" && (
          <label
            style={{
              marginLeft: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
            />
            Show only currently Active members
          </label>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          className="export-btn"
          onClick={handleGenerateAll}
          disabled={downloading}
          title="Generate all ministries' attendance/absent reports as a ZIP (CSV)"
        >
          {downloading ? "Generating..." : "📦 Generate All (ZIP)"}
        </button>

        <button
          className="export-btn"
          onClick={handleEmailReports}
          disabled={emailing}
          title="Email attendance + absent reports to leaders (server-configured)"
        >
          {emailing ? "Sending..." : "✉️ Email Reports"}
        </button>
      </div>

      <div className="report-table-container">
        {processedData.length === 0 ? (
          <p>No data available</p>
        ) : (
          <>
            <CSVLink
              data={csvData}
              filename={`${reportType}-report.csv`}
              className="export-btn"
            >
              ⬇️ Export CSV
            </CSVLink>
            <button
              className="export-btn"
              onClick={() => {
                const doc = new jsPDF();
                doc.text(`${reportType.toUpperCase()} Report`, 14, 10);
                autoTable(doc, {
                  head: [columns.map((key) => key.toUpperCase())],
                  body: processedData.map((row) =>
                    columns.map((key) => row[key] || "—")
                  ),
                  startY: 20,
                });
                doc.save(`${reportType}-report.pdf`);
              }}
            >
              🧾 Export PDF
            </button>

            <div className="table-wrapper">
              <table className="responsive-table">
                <thead>
                  <tr>
                    {columns.map((key) => (
                      <th
                        key={key}
                        onClick={() => requestSort(key)}
                        style={{ cursor: "pointer" }}
                      >
                        {key
                          .replace(/_/g, " ")
                          .replace("name", "Name")
                          .toUpperCase()}{" "}
                        {getSortIcon(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((row, i) => (
                    <tr key={i}>
                      {columns.map((key, j) => (
                        <td key={j}>
                          {key.toLowerCase().includes("date") && row[key]
                            ? new Date(row[key]).toLocaleDateString("en-US")
                            : key === "active"
                            ? row[key] === true ||
                              row[key] === "true" ||
                              row[key] === 1
                              ? "Active"
                              : "Inactive"
                            : row[key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsMaster;
